import {
  APIComponentAttributes,
  ArdoqId,
  APIScopeDataCore,
  AttachmentStrategies,
  ComponentBatchDeleteResponse,
  SubtreeInfo,
  MergeComponentApiResponse,
  CopyComponentsResponse,
  RestoreChangePayload,
  APIScopeData,
  APITagAttributes,
  PartialUpdateValues,
  APIComponentAndScopeData,
  APIComponentsAndScopeData,
  PartialBulkUpdatePayload,
  APIComponentPreviewScopeData,
  ProvenanceSource,
} from '@ardoq/api-types';
import { ApiResponse } from './types';
import {
  toRequestOptions,
  Method,
  updateEntityWithProvenanceSource,
  fetchJson,
  fetchEntity,
  createEntityWithProvenanceSource,
  getProvenanceHeaders,
} from './api';
import { isArdoqError, Result } from '@ardoq/common-helpers';

const deleteComponents = (
  componentIds: ArdoqId[],
  provenanceSource?: ProvenanceSource
): ApiResponse<ComponentBatchDeleteResponse> => {
  const request = new Request(
    `/api/component`,
    toRequestOptions(
      Method.DELETE,
      { componentIds },
      {
        extraHeaders: getProvenanceHeaders(provenanceSource),
      }
    )
  );
  return fetchJson<ComponentBatchDeleteResponse>(request);
};

const copyComponents = async (
  targetWorkspace: string,
  parent: string | undefined,
  componentIds: string[],
  attachmentStrategies?: AttachmentStrategies,
  provenanceSource?: ProvenanceSource
): ApiResponse<CopyComponentsResponse> => {
  const request = new Request(
    '/api/component/copy',
    toRequestOptions(
      Method.POST,
      {
        targetWorkspace,
        parent,
        componentIds,
        ...(attachmentStrategies ? { attachmentStrategies } : {}),
      },
      { extraHeaders: getProvenanceHeaders(provenanceSource) }
    )
  );
  return fetchJson<CopyComponentsResponse>(request);
};

type APIComponentScopeData = APIScopeDataCore & {
  tags: APITagAttributes[];
  scopeComponents: ArdoqId[];
};

export type APIScopeDataWithComponentId = APIScopeData & {
  componentId: ArdoqId;
};

const referenceSummary = (
  componentId: ArdoqId
): ApiResponse<APIScopeDataWithComponentId> => {
  const request = new Request(
    `/api/component/${componentId}/reference-summary`,
    toRequestOptions(Method.GET, undefined)
  );
  return fetchJson<APIScopeDataWithComponentId>(request);
};

const scopedata = async ({
  entityIds,
  includeAttachments = false,
}: {
  entityIds: string[];
  includeAttachments?: boolean;
}): ApiResponse<APIScopeData> => {
  const request = new Request(
    `/api/component/scopedata`,
    toRequestOptions(Method.POST, {
      'component-ids': entityIds,
      'include-attachments': includeAttachments,
    })
  );
  return fetchJson<APIComponentScopeData>(request).then(
    (result): Result<APIScopeData> => {
      if (isArdoqError(result)) {
        return result;
      }
      return result;
    }
  );
};

const previewScope = (
  componentId: ArdoqId
): ApiResponse<APIComponentPreviewScopeData> => {
  const request = new Request(
    `/api/component/${componentId}/preview-scope`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<APIComponentPreviewScopeData>(request);
};

const subtreeInfo = async (
  componentIds: ArdoqId[]
): ApiResponse<SubtreeInfo> => {
  const request = new Request(
    `/api/component/subtree-info`,
    toRequestOptions(Method.POST, { componentIds })
  );
  return fetchJson<SubtreeInfo>(request);
};

const generateDescription = async (body: {
  name: string;
  type: string;
  description: string | null;
  workspaceName?: string;
}): ApiResponse<{
  text: string;
  type: 'description' | 'excuse';
}> => {
  const request = new Request(
    `/api/component/autocomplete/description`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<{
    text: string;
    type: 'description' | 'excuse';
  }>(request);
};

const merge = (
  {
    mergedEntity,
    entityIdsToDelete,
  }: {
    mergedEntity: APIComponentAttributes;
    entityIdsToDelete: ArdoqId[];
  },
  provenanceSource?: ProvenanceSource
): ApiResponse<MergeComponentApiResponse> => {
  const request = new Request(
    `/api/component/${mergedEntity._id}/merge`,
    toRequestOptions(
      Method.PUT,
      {
        'subsumed-component-ids': entityIdsToDelete,
        'merged-component-data': mergedEntity,
      },
      { extraHeaders: getProvenanceHeaders(provenanceSource) }
    )
  );
  return fetchJson<MergeComponentApiResponse>(request);
};

const restore = async (
  payload: RestoreChangePayload,
  provenanceSource?: ProvenanceSource
): ApiResponse<APIComponentAttributes> => {
  const entityAttributes = await componentApi.fetch(payload.entityId);
  if (isArdoqError(entityAttributes)) {
    return entityAttributes;
  }
  return componentApi.updateComponent(
    {
      ...entityAttributes,
      [payload.fieldName]: payload.restorableValue,
    },
    provenanceSource
  );
};

export type ComponentPartialUpdateOptions = {
  /**
   * `mode=my_data` communicates to the api to use persona based permissions.
   * If used without the feature flag `Features.PERSON_IN_GRAPH_PERMISSIONS_PROTOTYPE`
   * the request will be rejected.
   * See https://ardoqcom.atlassian.net/wiki/x/J4CwDgE for more info
   */
  mode?: 'my_data';
};

/**
 * This endpoint uses different conflict resolution logic compared.
 * It does not check for version conflicts.
 * Instead, it checks if the oldValue of the updated field of the component
 * matches the current value in the database. If they match, the update is applied.
 * If not, this means that somebody else updated this field in the meantime
 * and the update is rejected.
 */
const partiallyUpdateComponent = async (
  id: ArdoqId,
  values: PartialUpdateValues,
  provenanceSource: ProvenanceSource,
  options: ComponentPartialUpdateOptions = {}
): ApiResponse<APIComponentAndScopeData> => {
  const params = new URLSearchParams(options);
  const request = new Request(
    `/api/component/${id}/partial-update?${params}`,
    toRequestOptions(Method.PUT, values, {
      extraHeaders: getProvenanceHeaders(provenanceSource),
    })
  );
  return fetchJson<APIComponentAndScopeData>(request);
};

/**
 * Partially bulk update multiple components.
 * All-or-nothing behavior: if any update fails, the entire operation fails and no changes are applied.
 */
const partiallyBulkUpdateComponents = async (
  values: PartialBulkUpdatePayload,
  options: ComponentPartialUpdateOptions = {},
  provenanceSource?: ProvenanceSource
): ApiResponse<APIComponentsAndScopeData> => {
  const params = new URLSearchParams(options);
  const request = new Request(
    `/api/component/partial-bulk-update?${params}`,
    toRequestOptions(
      Method.PUT,
      { updates: values },
      { extraHeaders: getProvenanceHeaders(provenanceSource) }
    )
  );
  return fetchJson<APIComponentsAndScopeData>(request);
};

export const componentApi = {
  deleteComponents,
  updateComponent:
    updateEntityWithProvenanceSource<APIComponentAttributes>('component'),
  createComponent:
    createEntityWithProvenanceSource<APIComponentAttributes>('component'),
  copyComponents,
  fetch: fetchEntity<APIComponentAttributes>('component'),
  referenceSummary,
  scopedata,
  previewScope,
  subtreeInfo,
  generateDescription,
  merge,
  restore,
  partiallyUpdateComponent,
  partiallyBulkUpdateComponents,
};
